import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";

const SIDE_IMG = "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative bg-navy-deep afha-grain">
        <div className="absolute inset-0 stripes-bg opacity-50" />
        <img src={SIDE_IMG} alt="Genetic laboratory" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Logo variant="light" />
          <div>
            <ShieldCheck className="h-10 w-10 text-patriot-light mb-5" />
            <h2 className="font-display text-4xl leading-tight tracking-tight">Physician onboarding made simple.</h2>
            <p className="mt-4 text-white/70 max-w-md">Manage your physician referrals from one professional dashboard.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-display text-3xl text-navy tracking-tight">Welcome back</h1>
          <p className="mt-2 text-muted-foreground">Sign in to manage your referrals.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-navy">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="mt-1.5 h-11" placeholder="you@example.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-navy">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="mt-1.5 h-11" placeholder="••••••••" />
              <div className="mt-2 text-right">
                <Link to="/forgot-password" className="text-sm font-medium text-patriot hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
            </div>
            {error && <p className="text-sm text-patriot" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-11 rounded-full bg-navy hover:bg-navy-dark text-white">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>

          <div className="mt-6 rounded-lg bg-muted border border-border p-4 text-sm text-muted-foreground">
            <p className="font-semibold text-navy mb-1">Demo account</p>
            rep@afha.com &nbsp;/&nbsp; rep123
          </div>

          <p className="mt-6 text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Link to="/signup" className="font-semibold text-patriot hover:underline" data-testid="go-signup">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
